<template>
    <div class="grid-container overflow-auto">
        <v-container class="form-content">
            <h4>{{ $t('titelbild') }} - {{ $t('predef_imgs') }}</h4>
            <v-row class="mt-5">
                <v-col cols="12" xl="8">
                    <label class="kp-label">{{ $t('dateiname') }}:</label>
                    <p>
                        <span v-if="imageRec">{{ imageRec.orig_filename }}</span>
                    </p>

                    <label class="kp-label mt-5">{{ $t('copyright') }}:</label>
                    <p>
                        <span v-if="imageRec">&copy; {{ imageRec.credits }}</span>
                    </p>

                    <div class="mb-5">
                        <LabelField :label="$t('caption_alt')">
                            <v-textarea
                                :value="record.featured_image_caption"
                                dense
                                outlined
                                hide-details="auto"
                                @input="record.set('featured_image_caption', $event)"
                            />
                        </LabelField>
                    </div>

                    <label class="kp-label mt-5">{{ $t('titel_alt') }}:</label>
                    <p>
                        <span v-if="imageRec">{{ imageRec.title }}</span>
                    </p>

                    <div>
                        <!-- change img infos btn -->
                        <ActionBtn
                            width="200px"
                            class="ma-1"
                            @click.stop="
                                () => {
                                    initialImage = imageRec;
                                    showImgCropDlgOnOpen = false;
                                    imgSelectDlg = true;
                                }
                            "
                        >
                            <KpIcon icon-cls="pencil" color="white" />
                            {{ $t('change_image_info') }}
                        </ActionBtn>
                        <!-- choose img btn -->
                        <ActionBtn
                            width="200px"
                            class="ma-1"
                            @click.stop="
                                () => {
                                    initialImage = null;
                                    showImgCropDlgOnOpen = false;
                                    imgSelectDlg = true;
                                }
                            "
                        >
                            <KpIcon icon-cls="mediamanager" color="white" />
                            {{ $t('choose_titleimg') }}
                        </ActionBtn>
                        <!-- crop img btn -->
                        <ActionBtn
                            width="200px"
                            class="ma-1"
                            @click.stop="
                                () => {
                                    initialImage = imageRec;
                                    showImgCropDlgOnOpen = true;
                                    imgSelectDlg = true;
                                }
                            "
                        >
                            <KpIcon icon-cls="pencil" color="white" />
                            {{ $t('change_titleimg') }}
                        </ActionBtn>
                        <!-- TODO: Freie Bilddatenbank -->
                        <!-- free img database btn -->
                        <!--
                        <ActionBtn
                            width="200px"
                            class="ma-1"
                            @click.stop="
                                initialImage = null;
                                showImgCropDlgOnOpen = false;
                                imgSelectDlg = true;
                            "
                        >
                            <KpIcon icon-cls="arrowrightup" color="white" disabled-color="grey" left />
                            {{ $t('free_image_database') }}
                        </ActionBtn>
                        -->
                    </div>
                </v-col>
            </v-row>
        </v-container>
        <!-- normal images, not animated -->
        <div v-if="imageRec && !imageRec.animated" class="images-container">
            <h4>{{ $t('img_formats') }}</h4>
            <div v-for="(conf, key) in imageConfig.magazin" :key="key" class="image-container mb-5">
                <label class="tgk-paragraph-text">{{ conf.text }} ({{ conf.width }}x{{ conf.height }})</label>
                <v-img
                    :max-width="conf.width"
                    class="mt-3"
                    :src="getImageUrl(record, conf)"
                    :alt="conf.text"
                    :title="conf.text"
                >
                    <img
                        v-if="conf.overlay_preview"
                        alt="overlay"
                        style="position: absolute; left: 0; top: 0; width: 100%; height: 100%"
                        :src="globalVar('webroot') + conf.overlay_preview"
                        class="overlaypreviewimage"
                    />
                </v-img>
            </div>
        </div>
        <!-- animated image -->
        <div v-if="imageRec && imageRec.animated" class="images-container">
            <h4>{{ $t('img_formats') }}</h4>
            <label class="tgk-paragraph-text">{{ $t('animated_img') }}</label>
            <v-img :src="imageRec.url" :alt="imageRec.title" :title="imageRec.title" :max-width="520" />
        </div>

        <MMSelectDialog
            v-if="imgSelectDlg"
            v-model="imgSelectDlg"
            :image-formats-config="imageConfig.magazin"
            :image-crop-data="record.featured_img_crop_config"
            :initial-image="initialImage"
            :initial-step="showImgCropDlgOnOpen ? 2 : 1"
            @image-select="onImageSelect"
            @image-crop="onImageCrop"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Magazin } from '@/models/Magazin';
import GlobalsMixin from '@/mixins/globals';
import MMSelectDialog from '@/components/media_manager/SelectFormatDialog.vue';
import { errToStr } from '@/helpers/tools';

export default {
    name: 'MagazinEditTitelbild',
    components: { MMSelectDialog },
    mixins: [GlobalsMixin],
    props: {
        record: {
            type: Magazin,
            required: true,
        },
    },
    data() {
        return {
            showImgCropDlgOnOpen: false,
            imgSelectDlg: false,
            initialImage: null,
            cacheBuster: new Date().getTime(),
        };
    },

    computed: {
        imageRec() {
            return this.$store.getters['image/imageById'](this.record.featured_image_id);
        },
    },

    watch: {
        record(act) {
            if (act && act.featured_image_id) {
                this.loadImage({ imageId: act.featured_image_id });
            }
        },
    },

    created() {
        // static view variables:
        this.imageConfig = this.globalVar('featured_images_config') || {};
    },

    mounted() {
        this.loadImage({ imageId: this.record.featured_image_id });
    },

    methods: {
        getImageUrl(magazinRec, imageConf) {
            return `${this.globalVar('imageroot')}/magazin/${magazinRec.id}/${imageConf.ident}.${imageConf.format}?ts=${
                this.cacheBuster
            }`;
        },

        ...mapActions('image', ['loadImage']),

        async onImageSelect(image) {
            if (image) {
                await this.onImageCrop({}, image, false);
            }
        },

        async onImageCrop(cropData, image, overrideCaption = true) {
            if (image) {
                try {
                    this.imgSelectDlg = false;
                    this.$emit('saving', true);
                    await this.createFeaturedImage(cropData, image, this.record);
                    this.record.set({
                        featured_image_id: image.id,
                        featured_img_crop_config: cropData || null,
                    });
                    if (overrideCaption) {
                        this.record.set({
                            featured_image_caption: image.caption || null,
                        });
                    }
                    this.cacheBuster = new Date().getTime();
                    this.loadImage({ imageId: image.id });
                    this.$emit('featured-image-created');
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                }
            }
        },

        async createFeaturedImage(cropData, image, record) {
            await image.createFeaturedImage('magazin', cropData, record.id);
        },
    },
};
</script>

<style lang="scss" scoped>
.grid-container {
    display: grid;
    grid-template-columns: 1fr minmax(300px, 1fr);
    grid-template-areas: 'form images';
}

.form-content {
    grid-area: form;
}
.images-container {
    padding: 10px;
    grid-area: images;
    background-color: var(--toolbar-bg-color);
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}
.image-container {
    width: 100%;
}
</style>
